// Recipe Module Phase 3 (docs/RECIPE_MODULE.md) — company-wide Knowledge Base: document library,
// chat, and AI recipe generation.
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const BASE = `${API_URL}/api/knowledge-base`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// ── Documents ────────────────────────────────────────────────────────────────

const listDocuments = () => axios.get(`${BASE}/documents`, authHeaders()).then(r => r.data);

const getDocumentUploadUrl = (contentType, fileSize) =>
  axios.post(`${BASE}/documents/upload-url`, { contentType, fileSize }, authHeaders()).then(r => r.data);

// SSE — saving a document now also extracts+chunks+embeds it (indexing), which can take well past
// Heroku's 30s router ceiling for a real book; see sseStream() below.
const saveDocument = (data, onProgress) =>
  sseStream(`${BASE}/documents`, data, onProgress).then(ev => ev.document);

const reindexDocument = (id, onProgress) =>
  sseStream(`${BASE}/documents/${id}/reindex`, {}, onProgress).then(ev => ev.document);

const deleteDocument = (id) => axios.delete(`${BASE}/documents/${id}`, authHeaders()).then(r => r.data);

const getDocumentDownloadUrl = (id) =>
  axios.get(`${BASE}/documents/${id}/download-url`, authHeaders()).then(r => r.data.url);

const uploadToPresignedUrl = (uploadUrl, file) =>
  axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });

const getChatHistory = (sessionId) =>
  axios.get(`${BASE}/chat/history`, { ...authHeaders(), params: { sessionId } }).then(r => r.data);

const getChatSessions = () => axios.get(`${BASE}/chat/sessions`, authHeaders()).then(r => r.data);

// ── SSE helper (mirrors maintenanceService.js's sseStream() exactly; duplicated deliberately
// rather than coupling services, same convention already used across this app) — used by document
// indexing, chat, and AI recipe generation, all of which can run long enough to hit Heroku's 30s
// router ceiling, which otherwise kills the connection and surfaces to the browser as a misleading
// CORS error. ─────────────────────────────────────────────────────────────────────────────────────

function sseStream(url, body, onProgress) {
  return new Promise(async (resolve, reject) => {
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(body),
      });
    } catch (e) { return reject(e); }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Request failed' }));
      return reject(err);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, '').trim();
          if (!line || line.startsWith(':')) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === 'progress') onProgress?.(ev.message);
            else if (ev.type === 'done') return resolve(ev);
            else if (ev.type === 'error') return reject(new Error(ev.message));
          } catch {}
        }
      }
    } catch (e) { reject(e); }
  });
}

const sendChatMessage = (question, sessionId) =>
  sseStream(`${BASE}/chat`, { question, sessionId });

const generateSampleRecipe = (recipeType, prompt, onProgress) =>
  sseStream(`${BASE}/generate-recipe`, { recipeType, prompt }, onProgress).then(ev => ev.recipe);

// Structures an already-written recipe (e.g. a chat answer's own text) into a real recipe draft,
// without inventing new ratios — used by the "➕ Add to your recipes" button on a chat message.
const convertTextToRecipe = (recipeType, text, onProgress) =>
  sseStream(`${BASE}/convert-to-recipe`, { recipeType, text }, onProgress).then(ev => ev.recipe);

const knowledgeBaseService = {
  listDocuments, getDocumentUploadUrl, saveDocument, reindexDocument, deleteDocument, getDocumentDownloadUrl, uploadToPresignedUrl,
  sendChatMessage, getChatHistory, getChatSessions,
  generateSampleRecipe, convertTextToRecipe,
};

export default knowledgeBaseService;
